import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { firmInfo } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1c1b] via-[#2a2c2b] to-[#1a1c1b]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
              GET IN
              <span className="block text-[#d9fb06]">TOUCH</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Ready to discuss your legal needs? Contact MAC Law Advocates today for professional legal representation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-[#302f2c]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-[#1a1c1b] border-[#3f4816] p-6 text-center hover:border-[#d9fb06] transition-all duration-300">
              <Phone className="w-10 h-10 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-white font-bold mb-2">Phone</h3>
              <a href={`tel:${firmInfo.phone}`} className="text-gray-400 hover:text-[#d9fb06] transition-colors">
                {firmInfo.phone}
              </a>
            </Card>
            <Card className="bg-[#1a1c1b] border-[#3f4816] p-6 text-center hover:border-[#d9fb06] transition-all duration-300">
              <Mail className="w-10 h-10 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-white font-bold mb-2">Email</h3>
              <a href={`mailto:${firmInfo.email}`} className="text-gray-400 hover:text-[#d9fb06] transition-colors break-all">
                {firmInfo.email}
              </a>
            </Card>
            <Card className="bg-[#1a1c1b] border-[#3f4816] p-6 text-center hover:border-[#d9fb06] transition-all duration-300">
              <MapPin className="w-10 h-10 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-white font-bold mb-2">Location</h3>
              <p className="text-gray-400">{firmInfo.address}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-black text-white mb-4">Send Us a Message</h2>
                <p className="text-gray-400 text-lg">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-semibold mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-[#302f2c] border-[#3f4816] text-white focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-semibold mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-[#302f2c] border-[#3f4816] text-white focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-white font-semibold mb-2">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-[#302f2c] border-[#3f4816] text-white focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    placeholder="+254 XXX XXX XXX"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-white font-semibold mb-2">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="bg-[#302f2c] border-[#3f4816] text-white focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-semibold mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="bg-[#302f2c] border-[#3f4816] text-white focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    placeholder="Tell us about your legal needs..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-base font-bold uppercase tracking-wide transition-all duration-300 hover:scale-105"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Office Info & Hours */}
            <div className="space-y-8">
              <Card className="bg-[#302f2c] border-[#3f4816] p-8">
                <h3 className="text-2xl font-bold text-[#d9fb06] mb-6">Office Location</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-[#d9fb06] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-1">Address</p>
                      <p className="text-gray-400">{firmInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Phone className="w-6 h-6 text-[#d9fb06] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-1">Phone</p>
                      <a href={`tel:${firmInfo.phone}`} className="text-gray-400 hover:text-[#d9fb06] transition-colors">
                        {firmInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="w-6 h-6 text-[#d9fb06] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-1">Email</p>
                      <a href={`mailto:${firmInfo.email}`} className="text-gray-400 hover:text-[#d9fb06] transition-colors break-all">
                        {firmInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#302f2c] border-[#3f4816] p-8">
                <h3 className="text-2xl font-bold text-[#d9fb06] mb-6">Office Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Monday - Friday</span>
                    <span className="text-gray-400">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Saturday</span>
                    <span className="text-gray-400">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Sunday</span>
                    <span className="text-gray-400">Closed</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-[#3f4816]">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-6 h-6 text-[#d9fb06] mt-1 flex-shrink-0" />
                    <p className="text-gray-400 text-sm">
                      Emergency consultations available by appointment. Contact us to schedule.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Google Maps */}
              <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg overflow-hidden h-64">
                <iframe
                  src={firmInfo.googleMapsUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MAC Law Advocates Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
